import { garmentTypesForGroup } from '@/data/garmentTypes';
import type { GarmentGroup, GarmentType } from '@/types/domain';

interface Props {
  group: GarmentGroup;
  value: GarmentType | null;
  onChange: (type: GarmentType | null) => void;
  /** 'grid' (default) is the full button grid, used on Brand vs. Brand. 'compact' is a single-line dropdown with identical options — used on the Dashboard, where vertical space is constrained. */
  variant?: 'grid' | 'compact';
  /** Only used by the compact variant — grid always shows its own label. */
  label?: string;
}

/**
 * Refines the broad garment group (tops/bottoms) down to a specific type
 * (T-Shirts, Jeans, ...), which sharpens the comparison's measurement
 * weighting — see src/data/garmentTypes.ts. Always optional: "General" (no
 * specific type) is a first-class choice, not a degraded fallback, since
 * that's the existing category-level comparison every current chart
 * supports today.
 */
export function GarmentTypeSelector({ group, value, onChange, variant = 'grid', label }: Props) {
  const types = garmentTypesForGroup(group);
  const generalLabel = group === 'tops' ? 'General top' : 'General bottom';

  if (variant === 'compact') {
    return (
      <label className="block">
        <span className="mb-1 block text-sm text-muted">{label ?? 'Garment type (optional)'}</span>
        <select
          value={value ?? ''}
          onChange={(e) => onChange((e.target.value || null) as GarmentType | null)}
          className="min-h-[46px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm"
        >
          <option value="">{generalLabel}</option>
          {types.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <div>
      <span className="mb-2 block text-sm font-medium text-ink">
        Garment type <span className="font-normal text-muted">(optional — sharpens the fit comparison)</span>
      </span>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Garment type">
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          onClick={() => onChange(null)}
          className={`min-h-[44px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
            value === null
              ? 'border-brand bg-brand text-ink shadow-card'
              : 'border-stone-200 bg-white text-ink hover:border-brand/40'
          }`}
        >
          {generalLabel}
        </button>
        {types.map((t) => (
          <button
            key={t.slug}
            type="button"
            role="radio"
            aria-checked={value === t.slug}
            onClick={() => onChange(t.slug)}
            className={`min-h-[44px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
              value === t.slug
                ? 'border-brand bg-brand text-ink shadow-card'
                : 'border-stone-200 bg-white text-ink hover:border-brand/40'
            }`}
          >
            {t.name}
          </button>
        ))}
      </div>
    </div>
  );
}
